import type { FC } from 'react';

import Info from '@crehana/compass.icons.fill/Info';
import Flag2 from '@crehana/compass.icons.outline/Flag2';
import { Download } from '@crehana/compass.icons.outline/v2/download';
import { PlayroomRewardIndicatorWithRive } from '@crehana/compass.playroom';
import { LazyImage } from '@crehana/compass.ui';
import { Button } from '@crehana/compass.ui/v3/button';
import { Message } from '@crehana/compass.ui/v3/message';
import { boldFormatter } from '@crehana/i18n';

import { COURSE_CARD_IMAGE_PARAMS } from '../../constants';
import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation/useTeamCoursesTranslation';

export type CertificateStatusCardProps = {
  trackName: string;
  portraitCertificate: string;
  rewardQuantity?: number;
  hasCertificate: boolean;
  attachmentUrl?: string | null;
  newCoursesCount: number;
  isFeatureEnabled: boolean;
  isCompletedTrack: boolean;
};

export const CertificateStatusCard: FC<CertificateStatusCardProps> = ({
  trackName,
  portraitCertificate,
  rewardQuantity,
  hasCertificate,
  attachmentUrl,
  newCoursesCount,
  isFeatureEnabled,
  isCompletedTrack,
}) => {
  const { t } = useTeamCoursesTranslation();
  const shouldRenderRewardIndicator =
    rewardQuantity !== undefined && rewardQuantity > 0;
  const hasNewCourses = isFeatureEnabled && newCoursesCount > 0;
  const shouldRenderDownloadButton = hasCertificate && Boolean(attachmentUrl);
  const shouldRenderDiplomaReplacementNotice =
    isFeatureEnabled && isCompletedTrack && hasNewCourses;
  const shouldRenderDiplomaPendingNotice =
    isFeatureEnabled && !isCompletedTrack && hasNewCourses;

  return (
    <section
      className="tw-w-full tw-rounded-1 tw-mt-1.6 dark:tw-bg-elevation-dark-04 tw-bg-neutral-light-100"
      data-testid="certificate-status-card"
    >
      <section className="tw-border-b tw-border-neutral-light-300 dark:tw-border-neutral-dark-600 tw-border-solid tw-mx-1.6 tw-pb-1.6 tw-pt-2.4 tw-px-0.8">
        <p className="tw-font-label1 tw-text-neutral-light-600 tw-uppercase">
          {isCompletedTrack
            ? t('LEARN_TEAM_TRACK_FINISHED_LABEL')
            : t('LEARN_TEAM_DIPLOMA_LABEL_PENDING')}
        </p>
      </section>

      <section className="md:tw-px-2 tw-pt-2.4 md:tw-pt-2 tw-pb-1.2 tw-p-1.2 sm:tw-p-1.6">
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-1.2">
          <div className="tw-w-2.7 tw-h-2.7 tw-rounded-full tw-bg-success-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
            <Flag2 size={16} className="tw-text-success-500" />
          </div>

          <div className="tw-relative tw-overflow-hidden tw-rounded-0.8 tw-h-8 tw-w-full sm:tw-w-[9.2rem] sm:tw-h-[5.2rem] tw-border tw-border-solid tw-border-success-500 tw-flex-shrink-0">
            <LazyImage
              src={portraitCertificate || ''}
              imgxParams={COURSE_CARD_IMAGE_PARAMS}
              alt={trackName || ''}
              isTransparent
              imgClassName="tw-object-cover"
              className="tw-w-full tw-h-full"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-0.4">
            <span className="tw-font-subtitle5 tw-text-success-500 dark:tw-text-neutral-light-100">
              {isCompletedTrack
                ? t('LEARN_TEAM_TRACK_SUCCESS')
                : t('LEARN_TEAM_DIPLOMA_TITLE_PENDING')}
            </span>
            <div className="tw-flex tw-justify-start tw-items-center tw-flex-row tw-gap-1">
              {shouldRenderRewardIndicator && (
                <PlayroomRewardIndicatorWithRive
                  coins={rewardQuantity}
                  className="tw-h-2.4"
                />
              )}
              {!hasCertificate && (
                <div className="tw-text-neutral-light-700 dark:tw-text-neutral-dark-400 tw-flex tw-justify-start tw-gap-0.8 tw-items-center tw-px-0.8 tw-py-[0.6rem] tw-border tw-border-neutral-light-500 tw-border-solid tw-rounded-0.8 tw-bg-neutral-light-200 tw-w-fit">
                  <Info size={16} className="tw-text-current" />
                  <p className="tw-font-body5">
                    {t('THIS_TRACK_DOES_NOT_PROVIDE_CERTIFICATE', {
                      formatter: boldFormatter,
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {shouldRenderDownloadButton && attachmentUrl && (
            <Button
              variant="secondary"
              size="sm"
              startIcon={Download}
              onClick={() =>
                window.open(attachmentUrl, '_blank', 'noopener,noreferrer')
              }
            >
              {t('LEARN_TEAM_DIPLOMA_DOWNLOAD_BUTTON')}
            </Button>
          )}
        </div>

        {shouldRenderDiplomaReplacementNotice && (
          <Message variant="info" className="tw-mt-1.2">
            {t('LEARN_TEAM_DIPLOMA_FUTURE_COURSES_ALERT')}
          </Message>
        )}
        {shouldRenderDiplomaPendingNotice && (
          <Message variant="warning" className="tw-mt-1.2">
            {t('LEARN_TEAM_DIPLOMA_PENDING_ALERT')}
          </Message>
        )}
      </section>
    </section>
  );
};
